import {
  formatSrsScheduleLabel,
  inferRating,
  isCardInLearningPhase,
  isPassingRecallAccuracy,
  isSuccessfulRating
} from '../../SRS/algorithm';
import { isSrsProgressRowDue } from '../../../services/reviewQueueService';

/** Payload committed when the user clicks Again (Anki-style lapse). */
export const AGAIN_SRS_PAYLOAD = {
  rating: 'again',
  accuracy: 0,
  initiallyWrongCount: 1,
  totalLines: 1
};

export const SRS_RECALL_RATING_LABELS = {
  easy: 'Easy recall',
  good: 'Good recall',
  hard: 'Hard recall',
  again: 'Needs practice'
};

const SHORT_RECALL_RATING_LABELS = {
  easy: 'Easy',
  good: 'Good',
  hard: 'Hard',
  again: 'Again'
};

/**
 * Build FSRS review metrics from a Check submission (regular study mode).
 *
 * @param {object} input
 * @param {{ accuracy: number, wrongLineCount: number, totalLines: number }} input.lineMetrics
 * @param {boolean} input.priorFailedChecks
 * @param {boolean} input.wasExactMatch
 */
export function buildCheckReviewMetrics({ lineMetrics, priorFailedChecks, wasExactMatch }) {
  const effectiveLineMetrics = wasExactMatch
    ? {
        ...lineMetrics,
        accuracy: 1,
        wrongLineCount: 0,
        matchingLines: lineMetrics.totalLines
      }
    : lineMetrics;

  const initiallyWrongCount = priorFailedChecks
    ? Math.max(1, effectiveLineMetrics.wrongLineCount)
    : 0;
  const rating = inferRating({
    accuracy: effectiveLineMetrics.accuracy,
    initiallyWrongCount,
    wrongLineCount: effectiveLineMetrics.wrongLineCount,
    totalLines: effectiveLineMetrics.totalLines,
    completed: wasExactMatch || isPassingRecallAccuracy(lineMetrics)
  });

  return {
    rating,
    accuracy: effectiveLineMetrics.accuracy,
    initiallyWrongCount,
    totalLines: effectiveLineMetrics.totalLines
  };
}

/**
 * Whether the inferred FSRS grade counts as passing recall (hard / good / easy).
 */
export function isRecallPassingMetrics(reviewMetrics) {
  return isSuccessfulRating(reviewMetrics?.rating);
}

/**
 * Whether SRS scheduling should run for this card (vs practice-only study).
 * Active when new, in learning/relearning, or due today; paused for up-to-date review cards.
 *
 * @param {Record<string, unknown> | null | undefined} progressSource
 * @param {Date} [now]
 */
export function isSrsSchedulingActive(progressSource, now = new Date()) {
  if (!progressSource || typeof progressSource !== 'object') return true;
  const totalReviews = Number(progressSource.totalReviews) || 0;
  if (totalReviews === 0) return true;
  if (isCardInLearningPhase(progressSource)) return true;
  return isSrsProgressRowDue(progressSource, now);
}

/**
 * First-exposure grace: brand-new cards defer the first SRS commit so the user can
 * orient to the snippet (variables, structure) without scheduling punishment.
 *
 * @param {Record<string, unknown> | null | undefined} progressSource
 */
export function isFirstExposurePending(progressSource) {
  if (!progressSource || typeof progressSource !== 'object') return true;
  const totalReviews = Number(progressSource.totalReviews) || 0;
  if (totalReviews > 0) return false;
  return progressSource.orientationCompleted !== true;
}

/**
 * Whether the next SRS write should be deferred in favor of orientation-only practice.
 *
 * @param {Record<string, unknown> | null | undefined} progressSource
 * @param {{ isTestMode?: boolean }} [options]
 */
export function shouldDeferFirstExposureSrs(progressSource, { isTestMode = false } = {}) {
  if (isTestMode) return false;
  return isFirstExposurePending(progressSource);
}

/**
 * Study UI / feedback: SRS is shown as practice-only (no schedule commit this action).
 *
 * @param {Record<string, unknown> | null | undefined} progressSource
 * @param {Date} [now]
 * @param {{ isTestMode?: boolean }} [options]
 */
export function isSrsPracticeOnlyForStudy(progressSource, now = new Date(), { isTestMode = false } = {}) {
  if (isTestMode) return false;
  if (shouldDeferFirstExposureSrs(progressSource, { isTestMode })) return true;
  return !isSrsSchedulingActive(progressSource, now);
}

/**
 * User-facing Check feedback from line accuracy + FSRS grade.
 */
export function formatCheckRecallFeedback({
  wasExactMatch = false,
  reviewMetrics,
  scheduleProgress = null,
  srsSchedulingPaused = false
}) {
  const rating = reviewMetrics?.rating || 'again';
  const accuracyPct = Math.round((reviewMetrics?.accuracy ?? 0) * 100);
  const ratingLabel = SHORT_RECALL_RATING_LABELS[rating] || rating;
  const scheduleLabel = scheduleProgress ? formatSrsScheduleLabel(scheduleProgress) : '';

  const parts = [`${accuracyPct}%`, ratingLabel];
  if (srsSchedulingPaused) {
    parts.push('practice only');
    return parts.join(' · ');
  }
  if (scheduleLabel) parts.push(scheduleLabel);
  if (!wasExactMatch && isRecallPassingMetrics(reviewMetrics)) {
    parts.push('scheduled · no EXP');
  }
  return parts.join(' · ');
}

function canCommitSrsVisit({
  studyMode,
  isTestMode,
  srsReviewCommitted,
  progressSource,
  rating,
  now
}) {
  if (studyMode !== 'regular' || isTestMode) return false;
  if (shouldDeferFirstExposureSrs(progressSource, { isTestMode })) return false;
  if (!isSrsSchedulingActive(progressSource, now)) return false;
  const inLearning = isCardInLearningPhase(progressSource);
  return !srsReviewCommitted || (inLearning && isSuccessfulRating(rating));
}

/**
 * Whether regular study mode should grade SRS on Check (handleAnswer path).
 */
export function shouldCommitSrsOnCheck({
  studyMode,
  isTestMode,
  srsReviewCommitted,
  progressSource,
  rating,
  now = new Date()
}) {
  return canCommitSrsVisit({
    studyMode,
    isTestMode,
    srsReviewCommitted,
    progressSource,
    rating,
    now
  });
}

/**
 * Whether commitDeckSrsReview should write to Firestore for this payload.
 */
export function shouldCommitDeckSrsPayload({
  studyMode,
  isTestMode,
  hasDeck,
  hasCard,
  srsReviewCommitted,
  progressSource,
  rating,
  now = new Date()
}) {
  if (!hasDeck || !hasCard) return false;
  return canCommitSrsVisit({
    studyMode,
    isTestMode,
    srsReviewCommitted,
    progressSource,
    rating,
    now
  });
}

/**
 * Options passed to SpacedRepetition.updateCardProgress for a deck commit.
 */
export function buildDeckSrsCommitOptions({ progressSource, rating }) {
  const inLearning = isCardInLearningPhase(progressSource);
  return {
    sameSessionContinue: inLearning && isSuccessfulRating(rating),
    markReviewCommitted: !inLearning || !isSuccessfulRating(rating)
  };
}

/**
 * Line-by-line completion must never touch SRS scheduling.
 */
export function isLineByLineSrsExcluded(studyMode) {
  return studyMode === 'lineByLine';
}
